import { useAppDispatch } from './state'
import {
  getClaims as getClaimsAPI,
  getClaim as getClaimAPI,
  deleteClaimConcept,
  createClaimConcept as createClaimAPI,
  updateClaimConcept as updateClaimAPI,
  uploadAdditionalClaimAttachment as uploadAdditionalClaimAttachmentAPI,
  removeAdditionalClaimAttachment as removeAdditionalClaimAttachment,
  storeClaim as storeClaimAPI
} from '../api/claims'
import {
  setClaims,
  updateClaim,
  deleteClaim as deleteClaimRedux,
  setConceptClaim,
  setCurrentStep,
  resetClaimDamageForm,
  setLatestClaimId,
  setConceptIsLoading,
  setFileIsLoading,
  setClaimIsLoading
} from '../state/slices/claims_slice'
import { IClaim } from '../models/claim'

export const useClaims = () => {
  const dispatch = useAppDispatch()

  const getClaims = async (): Promise<boolean> => {
    dispatch(setClaimIsLoading(true))
    try {
      const result = await getClaimsAPI()

      if (result.data) {
        dispatch(setClaims(result.data))
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setClaimIsLoading(false))
    }
  }

  const createClaimConcept = async (
    claim: Pick<
      IClaim,
      'agent_number' | 'relation_number' | 'branche' | 'policyNumber'
    >
  ) => {
    try {
      dispatch(setConceptIsLoading(true))
      const result = await createClaimAPI(claim)

      if (result.data) {
        dispatch(setConceptClaim(result.data))
      }

      return result.data
    } finally {
      dispatch(setConceptIsLoading(false))
    }
  }

  const updateClaimConcept = async (claim: IClaim) => {
    try {
      dispatch(setConceptIsLoading(true))
      const result = await updateClaimAPI(claim)

      if (result.data) {
        dispatch(setConceptClaim(result.data))
      }

      return result.data
    } finally {
      dispatch(setConceptIsLoading(false))
    }
  }

  const storeClaim = async (
    claim: Pick<
      IClaim,
      'id' | 'policyNumber' | 'dateOfLoss' | 'text' | 'phoneNumber'
    >
  ) => {
    try {
      const result = await storeClaimAPI(claim)

      if (result.data) {
        dispatch(updateClaim(result.data))
        dispatch(setConceptClaim(null))
        dispatch(setCurrentStep(3))
        dispatch(resetClaimDamageForm())
        dispatch(setLatestClaimId(result.data.id))
      }

      return result.data
    } catch (error) {
      return null
    }
  }

  const getClaim = async (id: string) => {
    try {
      dispatch(setClaimIsLoading(true))
      const result = await getClaimAPI(id)

      if (result.data) {
        dispatch(updateClaim(result.data))
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setClaimIsLoading(false))
    }
  }

  const deleteClaim = async (id: string) => {
    try {
      const result = await deleteClaimConcept(id)

      if (result.data) {
        dispatch(deleteClaimRedux(id))
      }

      return true
    } catch (error) {
      return false
    }
  }

  const uploadClaimAttachment = async (claimId: string, file: File) => {
    dispatch(setFileIsLoading(true))
    try {
      const result = await uploadAdditionalClaimAttachmentAPI(claimId, file)

      dispatch(setFileIsLoading(false))
      if (result.data) {
        const claim = await getClaimAPI(claimId)

        if (claim.data.status === 'Concept') {
          dispatch(setConceptClaim(claim.data))
        }
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setFileIsLoading(false))
    }
  }

  const deleteClaimAttachment = async (fileUrl: string, claimId: string) => {
    try {
      const result = await removeAdditionalClaimAttachment(fileUrl)

      if (result.data) {
        const claim = await getClaimAPI(claimId)

        if (claim.data.status === 'Concept') {
          dispatch(setConceptClaim(claim.data))
        }
      }
    } catch (error) {
      return false
    }
  }

  return {
    getClaims,
    getClaim,
    deleteClaim,
    createClaimConcept,
    uploadClaimAttachment,
    updateClaimConcept,
    deleteClaimAttachment,
    storeClaim
  }
}

export default useClaims
